import CloseRoundedIcon from "@mui/icons-material/Close";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { getChecklistReports } from "../../../../actions/checklists";
import ChecklistReferencePhotos from "./ChecklistReferencePhotos";
import { formatChecklistNumber } from "../../../../utils/checklistNumber";

const PAGE_SIZE_OPTIONS = [5, 10, 20];

const formatTimestamp = timestamp => {
  if (!timestamp) return "—";
  const date = new Date(timestamp);
  const pad = value => `${value}`.padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())} Uhr`;
};

// Rows: one per past report that answered this entry, newest/oldest first.
const buildRows = (reports, entryId) =>
  (reports || [])
    .map(report => {
      const item = (report.items || []).find(reportItem => reportItem.id === entryId);
      if (!item) return null;
      return {
        reportNodeId: report.nodeid,
        timestamp: item.timestamp || 0,
        checkboxvalue: item.checkboxvalue,
        numbervalue: item.numbervalue,
        textvalue: item.textvalue,
        // Each with a fresh signed url from this very read.
        photos: Array.isArray(item.photos) ? item.photos : []
      };
    })
    .filter(Boolean);

// connect()-wrapped so opening the dialog can dispatch getChecklistReports
// and read the result straight from redux, matching this feature's other
// checklist components.
export default connect(({ checklistReports }) => ({ checklistReports }), {
  getChecklistReports
})(props => {
  const { open, onClose, checklistNodeId, entry, checklistReports } = props;

  const [sortNewestFirst, setSortNewestFirst] = useState(true);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [page, setPage] = useState(1);
  // A report history call can take a while — without this the dialog just
  // shows "Keine vergangenen Antworten vorhanden." for that whole stretch,
  // indistinguishable from there genuinely being none.
  const [isLoadingReports, setIsLoadingReports] = useState(false);

  useEffect(() => {
    if (open && checklistNodeId) {
      setIsLoadingReports(true);
      // The reducer keys off this exact optype string ("GET_CHECKLIST_REPORTS",
      // matching the action type constant) to know where to store the
      // response — it is NOT just a descriptive label, unlike it may look.
      props.getChecklistReports(checklistNodeId, "GET_CHECKLIST_REPORTS");
      setSortNewestFirst(true);
      setPageSize(PAGE_SIZE_OPTIONS[0]);
      setPage(1);
    }
  }, [open, checklistNodeId]);

  // checklistReports is a fresh array on every response (see
  // extractChecklistReportsFromResponse), so this fires once the dispatch
  // above actually lands, whatever it came back with — including empty.
  useEffect(() => {
    setIsLoadingReports(false);
  }, [checklistReports]);

  const rows = useMemo(() => {
    const built = buildRows(checklistReports, entry?.id);
    return built.sort((a, b) =>
      sortNewestFirst ? b.timestamp - a.timestamp : a.timestamp - b.timestamp
    );
  }, [checklistReports, entry?.id, sortNewestFirst]);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = rows.slice(
    (currentPage - 1) * pageSize,
    (currentPage - 1) * pageSize + pageSize
  );

  const renderAnswer = row => {
    if (entry?.answerType === "number") {
      return (
        <Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}>
          {formatChecklistNumber(row.numbervalue) || "—"}
        </Typography>
      );
    }
    if (entry?.answerType === "text") {
      return (
        <Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}>
          {row.textvalue || "—"}
        </Typography>
      );
    }
    return (
      <Checkbox
        checked={Boolean(row.checkboxvalue)}
        disabled
        sx={{ p: 0, "&.Mui-disabled": { color: "#38748D" } }}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "6px",
          boxShadow:
            "0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2)",
          m: 2,
          p: "16px"
        }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={{ alignItems: "center", display: "flex", gap: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: "#0E3F51",
                fontFamily: "Bai Jamjuree, sans-serif",
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: "0.15px",
                lineHeight: 1.4
              }}
            >
              {`Vergangene Antworten zu „${entry?.title || ""}“`}
            </Typography>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 0.5)",
                fontFamily: "Roboto, sans-serif",
                fontSize: 12,
                letterSpacing: "0.4px"
              }}
            >
              {isLoadingReports
                ? "\u00A0"
                : `${rows.length} ${rows.length === 1 ? "Antwort" : "Antworten"}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "rgba(0,0,0,0.54)" }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <FormControl variant="standard" sx={{ width: 200 }}>
          <Typography
            sx={{
              color: "rgba(0, 0, 0, 0.5)",
              fontFamily: "Roboto, sans-serif",
              fontSize: 12,
              letterSpacing: "0.4px",
              mb: "5px"
            }}
          >
            Sortierung
          </Typography>
          <Select
            value={sortNewestFirst ? "newest" : "oldest"}
            onChange={event => {
              setSortNewestFirst(event.target.value === "newest");
              setPage(1);
            }}
            disableUnderline={false}
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 16,
              letterSpacing: "0.5px"
            }}
          >
            <MenuItem value="newest">Neueste zuerst</MenuItem>
            <MenuItem value="oldest">Älteste zuerst</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Typography
              sx={{
                color: "#0E3F51",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "0.1px",
                width: 142
              }}
            >
              Datum
            </Typography>
            <Typography
              sx={{
                color: "#0E3F51",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "0.1px",
                flex: 1
              }}
            >
              {entry?.title || ""}
            </Typography>
          </Box>
          <Box sx={{ borderTop: "1px solid rgba(0, 0, 0, 0.12)" }} />

          {isLoadingReports ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: "#0E3F51" }} />
            </Box>
          ) : pageRows.length === 0 ? (
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 0.5)",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                py: 2
              }}
            >
              Keine vergangenen Antworten vorhanden.
            </Typography>
          ) : (
            pageRows.map((row, index) => (
              <React.Fragment key={row.reportNodeId || index}>
                <Box sx={{ alignItems: "flex-start", display: "flex", gap: 3 }}>
                  <Typography
                    sx={{
                      color: "rgba(0, 0, 0, 0.87)",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 14,
                      letterSpacing: "0.25px",
                      width: 142
                    }}
                  >
                    {formatTimestamp(row.timestamp)}
                  </Typography>
                  {/* flex-start: a stretched checkbox would draw its tick mid-column. */}
                  <Box sx={{ alignItems: "flex-start", display: "flex", flex: 1, flexDirection: "column", gap: 1 }}>
                    {renderAnswer(row)}
                    <ChecklistReferencePhotos photos={row.photos} alt="Foto der Antwort" size={56} />
                  </Box>
                </Box>
                {index < pageRows.length - 1 ? (
                  <Box sx={{ borderTop: "1px solid rgba(0, 0, 0, 0.12)" }} />
                ) : null}
              </React.Fragment>
            ))
          )}
        </Box>

        {isLoadingReports ? null : (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <Pagination
            count={pageCount}
            page={currentPage}
            onChange={(_event, nextPage) => setPage(nextPage)}
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "rgba(0, 0, 0, 0.5)",
                fontFamily: "Bai Jamjuree, sans-serif"
              },
              "& .Mui-selected": {
                backgroundColor: "#0E3F51 !important",
                color: "#FFFFFF"
              }
            }}
          />

          <FormControl variant="standard" sx={{ width: 190 }}>
            <Select
              value={pageSize}
              onChange={event => {
                setPageSize(event.target.value);
                setPage(1);
              }}
              sx={{
                color: "rgba(0, 0, 0, 0.5)",
                fontFamily: "Roboto, sans-serif",
                fontSize: 16,
                letterSpacing: "0.5px"
              }}
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <MenuItem key={size} value={size}>
                  {`${size} Einträge pro Seite`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        )}
      </Box>
    </Dialog>
  );
});
